
import * as THREE from 'three';
import store from '../../redux/store';
import {
  selected_object,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import {
  startAnimation,
  stopAnimation,
  pauseAnimation,
} from './animationControls';
import { setLifeTimeout, clearLifeTimeout } from './lifeUtils';
import {
  createParticleTexture,
  applyParticleShapeMap,
  getDefaultParticleShape,
} from './particleShapeTextures';
import { hasValidParticleCount } from '../ThreeJsUtils/assetVisibilityUtils';
import { ensurePointsMaterialOrthoSupport, resyncSceneParticlePoints } from './syncParticlePointSizesForCamera';

/** Gentle drizzle — straight vertical fall only. */
const DRIZZLE_FALL_MIN = 0.06;
const DRIZZLE_FALL_RANGE = 0.1;

// ✅ Helper: clamp a particle back inside ±halfRange on X and Z.
const wrapParticleInBounds = (positions, i, halfRange) => {
  // X axis wrap
  if (positions[i] > halfRange) {
    positions[i] = -halfRange + (positions[i] - halfRange);
  } else if (positions[i] < -halfRange) {
    positions[i] = halfRange + (positions[i] + halfRange);
  }

  // Z axis wrap
  if (positions[i + 2] > halfRange) {
    positions[i + 2] = -halfRange + (positions[i + 2] - halfRange);
  } else if (positions[i + 2] < -halfRange) {
    positions[i + 2] = halfRange + (positions[i + 2] + halfRange);
  }
};

const createDrizzleVelocity = () => ({
  x: 0,
  y: -(Math.random() * DRIZZLE_FALL_RANGE + DRIZZLE_FALL_MIN),
  z: 0,
});

/** Full lifespan with variation; remaining life starts random so drops don't expire in waves. */
const createStaggeredLife = (baseLife, lifeVariation = 0.2) => {
  const life = Math.max(
    0.05,
    Number(baseLife) || 0
  );
  const variation = Math.max(0, Number(lifeVariation) || 0);
  const particleLife = Math.max(
    0.05,
    life * (1 + (Math.random() - 0.5) * variation)
  );
  return {
    maxLife: particleLife,
    // Continuous generation: desync each drop across its cycle
    remainingLife: Math.random() * particleLife,
  };
};

const assignParticleLife = (
  lifeArray,
  maxLifeArray,
  particleIndex,
  baseLife,
  lifeVariation,
  { stagger = false } = {}
) => {
  if (lifeArray[particleIndex] === undefined) return;

  const { maxLife, remainingLife } = createStaggeredLife(
    baseLife,
    lifeVariation
  );
  maxLifeArray[particleIndex] = maxLife;
  lifeArray[particleIndex] = stagger ? remainingLife : maxLife;
};

const stepRainDrizzle = ({
  positions,
  velocities,
  lifeArray,
  maxLifeArray,
  deltaTime,
  currentRange,
  currentHeight,
  currentLife,
  lifeVariation,
}) => {
  const halfRange = currentRange / 2;

  for (let i = 0; i < positions.length; i += 3) {
    const particleIndex = i / 3;

    if (lifeArray[particleIndex] !== undefined) {
      lifeArray[particleIndex] -= deltaTime;
    }

    // Pure vertical fall — no wind / turbulence / sideways velocity
    const vy = velocities[i + 1] ?? -DRIZZLE_FALL_MIN;
    positions[i + 1] += vy;
    velocities[i] = 0;
    velocities[i + 2] = 0;

    wrapParticleInBounds(positions, i, halfRange);

    const lifeExpired =
      lifeArray[particleIndex] !== undefined && lifeArray[particleIndex] <= 0;
    if (positions[i + 1] < -5 || lifeExpired) {
      positions[i] = (Math.random() - 0.5) * currentRange;
      positions[i + 1] = currentHeight;
      positions[i + 2] = (Math.random() - 0.5) * currentRange;

      if (
        lifeArray[particleIndex] !== undefined &&
        maxLifeArray[particleIndex] !== undefined
      ) {
        // Full new life on respawn — staggering is already from init + desynced reset times
        assignParticleLife(
          lifeArray,
          maxLifeArray,
          particleIndex,
          currentLife,
          lifeVariation,
          { stagger: false }
        );
      }

      const next = createDrizzleVelocity();
      velocities[i] = next.x;
      velocities[i + 1] = next.y;
      velocities[i + 2] = next.z;
    }
  }
};

const handleRain = (scene, particle) => {
  if (!scene) return;

  // Use dynamic properties
  const particleCount = particle?.particleCount !== undefined ? particle.particleCount : 1000;
  const range = particle?.range !== undefined ? particle.range : 50;
  const height = particle?.height !== undefined ? particle.height : 30;
  const color = particle?.color !== undefined ? particle.color : '#87CEEB';
  const opacity = particle?.opacity !== undefined ? particle.opacity : 0.7;
  const size = particle?.size !== undefined ? particle.size : 0.1;
  const windStrength = particle?.windStrength !== undefined ? particle.windStrength : 0.12;
  const turbulence = particle?.turbulence !== undefined ? particle.turbulence : 0.04;
  const shape = particle?.shape ?? getDefaultParticleShape('rain');
  const shapeRotation = particle?.shapeRotation !== undefined ? particle.shapeRotation : 0;
  const shapeScale = particle?.shapeScale !== undefined ? particle.shapeScale : 1.0;
  const life = particle?.life !== undefined ? particle.life : 3.0; // Rain life in seconds
  const lifeVariation = particle?.lifeVariation !== undefined ? particle.lifeVariation : 0.45;

  // Enhanced rain system
  const rainGroup = new THREE.Group();

  // Main raindrops
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(particleCount * 3);
  const velocities = new Float32Array(particleCount * 3);
  const sizes = new Float32Array(particleCount);
  const rotations = new Float32Array(particleCount);
  const lifeArray = new Float32Array(particleCount);
  const maxLifeArray = new Float32Array(particleCount);

  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * range;
    positions[i * 3 + 1] = Math.random() * height;
    positions[i * 3 + 2] = (Math.random() - 0.5) * range;

    // Gentle drizzle physics
    const drop = createDrizzleVelocity();
    velocities[i * 3] = drop.x;
    velocities[i * 3 + 1] = drop.y;
    velocities[i * 3 + 2] = drop.z;

    sizes[i] = Math.random() * 0.35 + 0.75;
    rotations[i] = 0;

    // Stagger remaining life so drops expire/respawn continuously (not in waves)
    assignParticleLife(lifeArray, maxLifeArray, i, life, lifeVariation, {
      stagger: true,
    });
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  const rainColor = particle?.color
    ? new THREE.Color(particle.color)
    : new THREE.Color(color);
  const material = new THREE.PointsMaterial({
    color: rainColor,
    size: size * shapeScale,
    transparent: true,
    opacity: opacity,
    sizeAttenuation: true,
    depthWrite: false,
    alphaTest: 0.01,
    map: createParticleTexture(shape, color, 64, shapeRotation, shapeScale),
  });
  material.userData = material.userData || {};
  material.userData.authoredSize = size * shapeScale;
  ensurePointsMaterialOrthoSupport(material);

  const rain = new THREE.Points(geometry, material);
  rain.userData.velocities = velocities;
  rain.userData.lifeArray = lifeArray;
  rain.userData.maxLifeArray = maxLifeArray;
  rainGroup.add(rain);

  rainGroup.name = 'rain';
  rainGroup.deleteType = 'particle';
  rainGroup.objType = 'rain';
  const uniqueId = `rain_${Date.now()}`;

  // Set the name based on particle data or generate unique ID
  if (particle && particle.id) {
    rainGroup.name = particle.id;
  } else if (particle && particle._id) {
    rainGroup.name = particle._id;
  } else {
    rainGroup.name = uniqueId;
  }

  rainGroup.displayName = 'Rain'; // For UI display
  rainGroup.url = uniqueId;
  rainGroup.asset_file_name = 'rain';
  rainGroup.file_name = 'rain';
  rainGroup.deleteType = 'particle';
  rainGroup.mesh_code = 'rain';
  rainGroup.glb_type = 'shapes';
  rainGroup.createdAt = new Date().toISOString();
  rainGroup.userData = {
    material_settings: {
      shader: 'Standard',
      albedo: color,
      roughness: 0.0,
      metalness: 0.0,
    },
    // Store dynamic properties for animation
    particleCount: particleCount,
    range: range,
    height: height,
    color: color,
    opacity: opacity,
    size: size,
    windStrength: windStrength,
    turbulence: turbulence,
    shape: shape,
    shapeRotation: shapeRotation,
    shapeScale: shapeScale,
    life: life,
    lifeVariation: lifeVariation,
    // Store life arrays for animation
    lifeArray: lifeArray,
    maxLifeArray: maxLifeArray,
  };

  // Set transform from particle data
  if (particle?.position) {
    rainGroup.position.set(
      particle.position.x || 0,
      particle.position.y || 0,
      particle.position.z || 0
    );
  }
  if (particle?.rotation) {
    rainGroup.rotation.set(
      particle.rotation.x || 0,
      particle.rotation.y || 0,
      particle.rotation.z || 0
    );
  }
  if (particle?.scale) {
    rainGroup.scale.set(
      particle.scale.x || 1,
      particle.scale.y || 1,
      particle.scale.z || 1
    );
  }
  rainGroup.file_name = particle?.file_name || 'rain';
  scene.add(rainGroup);
  resyncSceneParticlePoints(scene);
  store.dispatch(setPushIntersectedObject(rainGroup));

  // Gentle drizzle animation with thin-streak particles
  const animate = () => {
    if (!rainGroup.parent) return;
    const positions = geometry.attributes.position.array;
    const deltaTime = 0.016; // Approximate 60fps

    // Get current config from userData for dynamic updates
    const currentConfig = rainGroup.userData;
    const currentRange = currentConfig?.range !== undefined ? currentConfig.range : range;
    const currentHeight = currentConfig?.height !== undefined ? currentConfig.height : height;
    const currentLife = currentConfig?.life !== undefined ? currentConfig.life : life;

    stepRainDrizzle({
      positions,
      velocities,
      lifeArray,
      maxLifeArray,
      deltaTime,
      currentRange,
      currentHeight,
      currentLife,
      lifeVariation: currentConfig?.lifeVariation ?? lifeVariation,
    });

    geometry.attributes.position.needsUpdate = true;
    rainGroup.userData.animationId = requestAnimationFrame(animate);
  };

  // Store the animation function in userData for later use
  rainGroup.userData.animateFunction = animate;

  // Start animation if particle is not stopped
  if (!particle?.isStopped) {
    startAnimation(rainGroup, animate, rainGroup.name);
    setLifeTimeout(
      rainGroup,
      typeof life === 'number' ? life : parseFloat(life) || 0
    );
  }

  store.dispatch(selected_object(rainGroup));
  selectObjectInScene(rainGroup.name);
};

const updateRain = (scene, rainSystem, newParticleData) => {
  if (!scene || !rainSystem || rainSystem.objType !== 'rain') {
    console.warn('Invalid rain system or scene provided for update');
    return;
  }

  

  // Get current configuration
  const currentConfig = rainSystem.userData || {};

  // Merge current config with new particle data
  const updatedConfig = {
    particleCount: newParticleData?.particleCount !== undefined ? newParticleData.particleCount : (currentConfig.particleCount !== undefined ? currentConfig.particleCount : 1000),
    range: newParticleData?.range !== undefined ? newParticleData.range : (currentConfig.range !== undefined ? currentConfig.range : 50),
    height: newParticleData?.height !== undefined ? newParticleData.height : (currentConfig.height !== undefined ? currentConfig.height : 50),
    color: newParticleData?.color !== undefined ? newParticleData.color : (currentConfig.color !== undefined ? currentConfig.color : '#87CEEB'),
    opacity: newParticleData?.opacity !== undefined ? newParticleData.opacity : (currentConfig.opacity !== undefined ? currentConfig.opacity : 0.7),
    size: newParticleData?.size !== undefined ? newParticleData.size : (currentConfig.size !== undefined ? currentConfig.size : 0.1),
    windStrength: newParticleData?.windStrength !== undefined ? newParticleData.windStrength : (currentConfig.windStrength !== undefined ? currentConfig.windStrength : 0.12),
    turbulence: newParticleData?.turbulence !== undefined ? newParticleData.turbulence : (currentConfig.turbulence !== undefined ? currentConfig.turbulence : 0.04),
    shape:
      newParticleData?.shape ??
      currentConfig.shape ??
      getDefaultParticleShape('rain'),
    shapeRotation: newParticleData?.shapeRotation !== undefined ? newParticleData.shapeRotation : (currentConfig.shapeRotation !== undefined ? currentConfig.shapeRotation : 0),
    shapeScale: newParticleData?.shapeScale !== undefined ? newParticleData.shapeScale : (currentConfig.shapeScale !== undefined ? currentConfig.shapeScale : 1.0),
    life: newParticleData?.life !== undefined ? newParticleData.life : (currentConfig.life !== undefined ? currentConfig.life : 3.0),
    lifeVariation: newParticleData?.lifeVariation !== undefined ? newParticleData.lifeVariation : (currentConfig.lifeVariation !== undefined ? currentConfig.lifeVariation : 0.45),
  };

  
  // Handle animation state changes
  if (
    typeof newParticleData?.isPlaying !== 'undefined' ||
    typeof newParticleData?.isStopped !== 'undefined'
  ) {
    const isPlaying =
      newParticleData?.isPlaying ?? currentConfig.isPlaying ?? true;
    const isStopped =
      newParticleData?.isStopped ?? currentConfig.isStopped ?? false;

    if (isStopped) {
      stopAnimation(rainSystem, rainSystem.name);
    } else if (isPlaying) {
      const animateFunction = rainSystem.userData.animateFunction;
      if (animateFunction) {
        startAnimation(rainSystem, animateFunction, rainSystem.name);
      }
    } else {
      pauseAnimation(rainSystem, rainSystem.name);
    }
  }

  // Check if particle count changed - requires full recreation
  const rainPoints = rainSystem.children.find(
    (child) => child instanceof THREE.Points
  );
  if (
    rainPoints &&
    hasValidParticleCount(updatedConfig.particleCount) &&
    updatedConfig.particleCount !== currentConfig.particleCount
  ) {
    // Dispose old geometry
    rainPoints.geometry.dispose();

    // Create new geometry with updated particle count
    const newGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(updatedConfig.particleCount * 3);
    const velocities = new Float32Array(updatedConfig.particleCount * 3);
    const sizes = new Float32Array(updatedConfig.particleCount);
    const rotations = new Float32Array(updatedConfig.particleCount);
    const lifeArray = new Float32Array(updatedConfig.particleCount);
    const maxLifeArray = new Float32Array(updatedConfig.particleCount);

    for (let i = 0; i < updatedConfig.particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * updatedConfig.range;
      positions[i * 3 + 1] = Math.random() * updatedConfig.height;
      positions[i * 3 + 2] = (Math.random() - 0.5) * updatedConfig.range;

      // Gentle drizzle physics
      const drop = createDrizzleVelocity();
      velocities[i * 3] = drop.x;
      velocities[i * 3 + 1] = drop.y;
      velocities[i * 3 + 2] = drop.z;

      sizes[i] = Math.random() * 0.35 + 0.75;
      rotations[i] = 0;

      // Stagger remaining life so rebuilds also emit continuously
      assignParticleLife(
        lifeArray,
        maxLifeArray,
        i,
        updatedConfig.life,
        updatedConfig.lifeVariation,
        { stagger: true }
      );
    }

    newGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3)
    );
    newGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    rainPoints.geometry = newGeometry;

    // Store velocities and life arrays for animation
    rainPoints.userData.velocities = velocities;
    rainPoints.userData.rotations = rotations;
    rainPoints.userData.lifeArray = lifeArray;
    rainPoints.userData.maxLifeArray = maxLifeArray;
  }

  const rangeChanged = newParticleData?.range !== undefined && newParticleData.range !== currentConfig.range;
  const heightChanged = newParticleData?.height !== undefined && newParticleData.height !== currentConfig.height;

  // Scale positions if range/height changed (same particle count)
  if (
    rainPoints &&
    rainPoints.geometry &&
    (rangeChanged || heightChanged) &&
    updatedConfig.particleCount === currentConfig.particleCount
  ) {
    const positions = rainPoints.geometry.attributes.position.array;
    
    // Scale existing positions to smoothly expand/contract without jarring resets
    const rangeRatio = rangeChanged ? (updatedConfig.range / (currentConfig.range || 1)) : 1;
    const heightRatio = heightChanged ? (updatedConfig.height / (currentConfig.height || 1)) : 1;

    for (let i = 0; i < positions.length; i += 3) {
      if (rangeChanged) {
        positions[i]     *= rangeRatio;
        positions[i + 2] *= rangeRatio;
      }
      if (heightChanged) {
        positions[i + 1] *= heightRatio;
      }
    }
    rainPoints.geometry.attributes.position.needsUpdate = true;
    rainPoints.geometry.computeBoundingBox();
    rainPoints.geometry.computeBoundingSphere();
    
    // Force TransformControls to update its bounding box gizmo immediately
    if (rainSystem.userData) rainSystem.userData.forceBoxUpdate = true;
  }

  // Update material properties
  if (rainPoints && rainPoints.material) {
    const newColor = new THREE.Color(updatedConfig.color);
    rainPoints.material.color.copy(newColor);
    if (newParticleData?.opacity !== undefined) {
      rainPoints.material.opacity = updatedConfig.opacity;
      rainPoints.material.transparent = updatedConfig.opacity < 1;
      rainPoints.material.needsUpdate = true;
    }
    if (newParticleData?.size !== undefined) {
      rainPoints.material.size = updatedConfig.size * updatedConfig.shapeScale;
    }
    if (
      newParticleData?.shape !== undefined ||
      newParticleData?.color !== undefined ||
      newParticleData?.shapeRotation !== undefined ||
      newParticleData?.shapeScale !== undefined
    ) {
      applyParticleShapeMap(
        rainPoints.material,
        updatedConfig.shape,
        updatedConfig.color,
        updatedConfig.shapeRotation,
        updatedConfig.shapeScale
      );
    }
  }

  // Update transforms if provided
  if (newParticleData?.position) {
    rainSystem.position.set(
      newParticleData.position.x !== undefined
        ? newParticleData.position.x
        : rainSystem.position.x,
      newParticleData.position.y !== undefined
        ? newParticleData.position.y
        : rainSystem.position.y,
      newParticleData.position.z !== undefined
        ? newParticleData.position.z
        : rainSystem.position.z
    );
  }
  if (newParticleData?.rotation) {
    rainSystem.rotation.set(
      newParticleData.rotation.x !== undefined
        ? newParticleData.rotation.x
        : rainSystem.rotation.x,
      newParticleData.rotation.y !== undefined
        ? newParticleData.rotation.y
        : rainSystem.rotation.y,
      newParticleData.rotation.z !== undefined
        ? newParticleData.rotation.z
        : rainSystem.rotation.z
    );
  }
  if (newParticleData?.scale) {
    rainSystem.scale.set(
      newParticleData.scale.x !== undefined
        ? newParticleData.scale.x
        : rainSystem.scale.x,
      newParticleData.scale.y !== undefined
        ? newParticleData.scale.y
        : rainSystem.scale.y,
      newParticleData.scale.z !== undefined
        ? newParticleData.scale.z
        : rainSystem.scale.z
    );
  }

  // Update userData with new configuration
  rainSystem.userData = {
    ...rainSystem.userData,
    ...updatedConfig,
    material_settings: {
      ...rainSystem.userData.material_settings,
      albedo: updatedConfig.color,
    },
    // Preserve life arrays if they exist
    lifeArray: rainSystem.userData.lifeArray || rainPoints?.userData?.lifeArray,
    maxLifeArray:
      rainSystem.userData.maxLifeArray || rainPoints?.userData?.maxLifeArray,
  };

  // Update system metadata if ID changed
  if (newParticleData?.id && newParticleData.id !== rainSystem.name) {
    rainSystem.name = newParticleData.id;
    rainSystem.url = newParticleData.id;
  }

  // Restart animation if particle count changed and animation should be running
  if (
    hasValidParticleCount(updatedConfig.particleCount) &&
    updatedConfig.particleCount !== currentConfig.particleCount &&
    !newParticleData?.isStopped &&
    newParticleData?.isPlaying !== false
  ) {
    // Stop existing animation if it exists
    if (rainSystem.userData.animationId) {
      cancelAnimationFrame(rainSystem.userData.animationId);
    }

    // Restart animation with updated configuration
    const animate = () => {
      if (!rainSystem.parent) {
        rainSystem.userData.animationId = null;
        return;
      }

      const rainPoints = rainSystem.children.find(
        (child) => child instanceof THREE.Points
      );
      if (!rainPoints || !rainPoints.geometry) return;

      const positions = rainPoints.geometry.attributes.position.array;
      const velocities = rainPoints.userData.velocities || [];
      const lifeArray = rainPoints.userData.lifeArray || rainSystem.userData.lifeArray || [];
      const maxLifeArray = rainPoints.userData.maxLifeArray || rainSystem.userData.maxLifeArray || [];
      const deltaTime = 0.016; // Approximate 60fps

      // Read current config dynamically from userData so updates apply immediately
      const activeConfig = rainSystem.userData;
      const currentRange = activeConfig?.range !== undefined ? activeConfig.range : updatedConfig.range;
      const currentHeight = activeConfig?.height !== undefined ? activeConfig.height : updatedConfig.height;
      const currentLife = activeConfig?.life !== undefined ? activeConfig.life : updatedConfig.life;

      stepRainDrizzle({
        positions,
        velocities,
        lifeArray,
        maxLifeArray,
        deltaTime,
        currentRange,
        currentHeight,
        currentLife,
        lifeVariation: activeConfig?.lifeVariation ?? updatedConfig.lifeVariation,
      });

      rainPoints.geometry.attributes.position.needsUpdate = true;
      rainSystem.userData.animationId = requestAnimationFrame(animate);
    };

    // Store the updated animation function
    rainSystem.userData.animateFunction = animate;
    rainSystem.userData.animationId = requestAnimationFrame(animate);
  }

  // Only dispatch update event, don't change selection
  store.dispatch(setPushIntersectedObject(rainSystem));

  // Manage life timeout: clear previous then set new if provided
  try {
    clearLifeTimeout(rainSystem);
    const lifeVal = updatedConfig?.life;
    const lifeSeconds =
      typeof lifeVal === 'number' ? lifeVal : parseFloat(lifeVal);
    if (!isNaN(lifeSeconds) && lifeSeconds > 0) {
      setLifeTimeout(rainSystem, lifeSeconds);
      rainSystem.visible = true;
      if (rainSystem.userData) {
        rainSystem.userData.isStopped = false;
        rainSystem.userData.isPlaying = true;
      }
    }
  } catch (e) {
    // ignore
  }

  resyncSceneParticlePoints(scene);
  return rainSystem;
};

// Helper function to find rain system by name/id in scene
const findRainSystem = (scene, nameOrId) => {
  return scene.children.find(
    (child) =>
      (child.name === nameOrId || child.url === nameOrId) &&
      child.objType === 'rain'
  );
};

// Convenience function to update rain by name
const updateRainByName = (scene, nameOrId, newParticleData) => {
  const rainSystem = findRainSystem(scene, nameOrId);
  if (rainSystem) {
    return updateRain(scene, rainSystem, newParticleData);
  } else {
    console.warn(`Rain system with name/id "${nameOrId}" not found in scene`);
    return null;
  }
};

export default handleRain;
export { updateRain, updateRainByName };